import { useState, useEffect } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Trash2, Pencil, Plus, AlertCircle, BarChart4 } from "lucide-react";
import { Dialog } from "@/components/ui/dialog";
import { useData } from "@/hooks/useData";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { formatNumber } from "@/utils/numberFormatter";
import { ElectricityModalForm } from "./ElectricityModalForm";
import { Badge } from "@/components/ui/badge";
import {
  ElectricityEntry,
  ElectricityPayload,
  ElectricitySourceDoc,
  ElectricitySourceDocWithId,
} from "@/types/electricity";
import {
  calculateElectricityCarbonFootprint,
  calculateElectricityHydricFootprint,
  calculateTotalElectricityCarbonFootprint,
  calculateTotalElectricityHydricFootprint,
} from "../../utils/calculations/annualConsumptions";

// Empty electricity entry for new entries
const emptyEntry: ElectricityEntry = {
  energySource: "",
  quantity: "",
  unit: "kWh",
  pc_ele: 0,
  ph_ele: 0,
};

interface ElectricityConsumptionStepProps {
  existingData?: ElectricityPayload;
  onDataChange: (payload: ElectricityPayload) => void;
  showTitle?: boolean;
  checkboxId?: string;
}

export const ElectricityConsumptionStep = ({
  existingData,
  onDataChange,
  showTitle = true,
  checkboxId = "noElectricityConsumption",
}: ElectricityConsumptionStepProps) => {
  const { t } = useLanguage();

  // Initialize entries array from existingData.entries, if provided
  const [electricityEntries, setElectricityEntries] = useState<ElectricityEntry[]>(
    Array.isArray(existingData?.entries) ? existingData.entries : []
  );
  const [noElectricityConsumption, setNoElectricityConsumption] = useState<boolean>(
    existingData?.noElectricityConsumption || false
  );
  const [modalOpen, setModalOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [formEntry, setFormEntry] = useState<ElectricityEntry>(emptyEntry);

  // Fetch electricity source docs
  const { data: electricitySources } = useData<ElectricitySourceDocWithId>({
    collectionName: "setup_energyConsumption",
  });

  // Get electricity source options not already selected
  const sourceOptions = (electricitySources || [])
    .map((doc) => ({ value: doc.name, label: doc.name }))
    .filter((opt) =>
      // Only filter if multiple sources are defined in setup
      electricitySources && electricitySources.length > 1
        ? !electricityEntries.some((e) => e.energySource === opt.value)
        : true
    );

  // Sync up if existingData changes
  useEffect(() => {
    const entries = Array.isArray(existingData?.entries) ? existingData.entries : [];
    setElectricityEntries(entries);
    setNoElectricityConsumption(existingData?.noElectricityConsumption || false);
  }, [existingData]);

  /** Find electricity source document by name */
  const getSourceDoc = (sourceName: string): ElectricitySourceDoc | undefined => {
    return (electricitySources || []).find((doc) => doc.name === sourceName);
  };

  // Recalculate footprints when electricitySources are loaded or changed
  useEffect(() => {
    if (electricitySources && electricitySources.length > 0 && electricityEntries.length > 0) {
      let hasChanges = false;

      const updatedEntries = electricityEntries.map(entry => {
        // Skip entries without quantity
        if (!entry.quantity || Number(entry.quantity) === 0) {
          return entry;
        }

        const sourceDoc = getSourceDoc(entry.energySource);
        if (!sourceDoc) {
          return entry;
        }

        // Always recalculate using current electricity source data
        const carbonResult = calculateElectricityCarbonFootprint(entry, sourceDoc);
        const hydricResult = calculateElectricityHydricFootprint(entry, sourceDoc);

        // Check if values changed
        if (
          entry.pc_ele !== carbonResult.footprint ||
          entry.ph_ele !== hydricResult.footprint ||
          entry.carbonFactor !== carbonResult.factor ||
          entry.hydricFactor !== hydricResult.factor
        ) {
          hasChanges = true;
          return {
            ...entry,
            pc_ele: carbonResult.footprint,
            ph_ele: hydricResult.footprint,
            carbonFactor: carbonResult.factor,
            hydricFactor: hydricResult.factor,
          };
        }

        return entry;
      });

      // Only update if something actually changed
      if (hasChanges) {
        setElectricityEntries(updatedEntries);
        onDataChange({
          entries: updatedEntries,
          total_pc_ele: calculateTotalElectricityCarbonFootprint(updatedEntries),
          total_ph_ele: calculateTotalElectricityHydricFootprint(updatedEntries),
          noElectricityConsumption,
        });
      }
    }
  }, [electricitySources, electricityEntries, getSourceDoc, onDataChange, noElectricityConsumption]);

  /** Handle checkbox for no electricity consumption */
  const handleNoElectricityConsumptionChange = (checked: boolean) => {
    setNoElectricityConsumption(checked);
    if (checked) {
      // Clear all entries when checkbox is checked
      setElectricityEntries([]);
      onDataChange({
        entries: [],
        total_pc_ele: 0,
        total_ph_ele: 0,
        noElectricityConsumption: true,
      });
    } else {
      onDataChange({
        entries: electricityEntries,
        total_pc_ele: calculateTotalElectricityCarbonFootprint(electricityEntries),
        total_ph_ele: calculateTotalElectricityHydricFootprint(electricityEntries),
        noElectricityConsumption: false,
      });
    }
  };

  /** Open form modal (add vs edit) */
  const openModal = (entry?: ElectricityEntry, idx?: number) => {
    if (entry && typeof idx === "number") {
      setFormEntry({ ...entry });
      setEditingIndex(idx);
    } else {
      setFormEntry(emptyEntry);
      setEditingIndex(null);
    }
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setTimeout(() => {
      setFormEntry(emptyEntry);
      setEditingIndex(null);
    }, 100);
  };

  /** Save (add/edit) */
  const handleSave = () => {
    const sourceDoc = getSourceDoc(formEntry.energySource);

    // Calculate footprints and get factors used
    const carbonResult = calculateElectricityCarbonFootprint(formEntry, sourceDoc);
    const hydricResult = calculateElectricityHydricFootprint(formEntry, sourceDoc);

    const entryWithFootprints: ElectricityEntry = {
      ...formEntry,
      pc_ele: carbonResult.footprint,
      ph_ele: hydricResult.footprint,
      carbonFactor: carbonResult.factor,
      hydricFactor: hydricResult.factor,
    };

    const newEntries = [...electricityEntries];
    if (editingIndex !== null) newEntries[editingIndex] = entryWithFootprints;
    else newEntries.push(entryWithFootprints);

    setElectricityEntries(newEntries);
    onDataChange({
      entries: newEntries,
      total_pc_ele: calculateTotalElectricityCarbonFootprint(newEntries),
      total_ph_ele: calculateTotalElectricityHydricFootprint(newEntries),
      noElectricityConsumption,
    });
    closeModal();
  };

  /** Remove one entry */
  const handleRemoveEntry = (index: number) => {
    const newEntries = electricityEntries.filter((_, i) => i !== index);
    setElectricityEntries(newEntries);
    onDataChange({
      entries: newEntries,
      total_pc_ele: calculateTotalElectricityCarbonFootprint(newEntries),
      total_ph_ele: calculateTotalElectricityHydricFootprint(newEntries),
      noElectricityConsumption,
    });
  };

  const handleFormEntryChange = (entry: ElectricityEntry) => {
    // Set unit to kWh
    entry.unit = "kWh";
    setFormEntry(entry);
  };

  return (
    <div className="space-y-6">
      {/* Title + Add button */}
      {showTitle ? (
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">{t("footprints.categories.electricity.title")}</h2>
            <p className="text-sm text-gray-500 mt-1">{t("footprints.guidelines.electricity")}</p>
          </div>
          <Button
            onClick={() => openModal()}
            variant="outline"
            className="gap-2 bg-teal-600 text-white hover:bg-teal-700 hover:text-white"
            disabled={noElectricityConsumption}
          >
            <Plus className="h-4 w-4" />
            {t("common.add")}
          </Button>
        </div>
      ) : (
        <div className="flex justify-end mb-4">
          <Button
            onClick={() => openModal()}
            variant="outline"
            className="gap-2 bg-teal-600 text-white hover:bg-teal-700 hover:text-white"
            disabled={noElectricityConsumption}
          >
            <Plus className="h-4 w-4" />
            {t("common.add")}
          </Button>
        </div>
      )}

      {/* No Electricity Consumption Checkbox - only show when there are no entries */}
      {electricityEntries.length === 0 && (
        <div className="flex items-center space-x-2 p-4 bg-gray-50 border border-gray-200 rounded-lg">
          <Checkbox
            id={checkboxId}
            checked={noElectricityConsumption}
            onCheckedChange={handleNoElectricityConsumptionChange}
          />
          <label
            htmlFor={checkboxId}
            className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
          >
            {t("electricity.noElectricityConsumption")}
          </label>
        </div>
      )}

      {/* No entries / list of cards */}
      {electricityEntries.length === 0 ? (
        <Alert variant="default" className="bg-gray-50 border-gray-200">
          <div className="flex items-center">
            <AlertCircle className="h-5 w-5 text-gray-400 mr-2" />
            <AlertDescription className="text-gray-500">{t("electricity.noEntries")}</AlertDescription>
          </div>
        </Alert>
      ) : (
        <>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {electricityEntries.map((entry, idx) => (
              <Card key={idx} className="flex flex-col h-full shadow-sm hover:shadow transition-shadow">
                <CardHeader className="bg-gray-50 pb-3">
                  <div className="flex justify-between items-center">
                    <CardTitle className="text-lg font-semibold">{entry.energySource}</CardTitle>
                    <div className="flex space-x-1">
                      <Button size="sm" variant="ghost" onClick={() => openModal(entry, idx)}>
                        <Pencil className="h-4 w-4 text-gray-500 hover:text-teal-600" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => handleRemoveEntry(idx)}>
                        <Trash2 className="h-4 w-4 text-gray-500 hover:text-red-600" />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="py-4">
                  <div className="space-y-2">
                    <div className="flex justify-between items-center mb-0 pb-4">
                      <span className="text-sm text-gray-500">{t("common.quantity")}:</span>
                      <Badge
                        variant="secondary"
                        className="bg-gray-50 text-gray-700 hover:bg-gray-50 hover:text-gray-700"
                      >
                        {formatNumber(Number(entry.quantity))} {entry.unit}
                      </Badge>
                    </div>
                  </div>
                </CardContent>

                <div className="mt-auto">
                  <div className="bg-teal-50/80 border-t py-3 px-4">
                    <div className="flex justify-between items-center text-teal-700 w-full">
                      <div className="flex items-center gap-1.5">
                        <BarChart4 className="h-5 w-5 align-middle" />
                        <span className="align-middle">{t("common.carbonFootprint")}</span>
                      </div>
                      <span className="text-lg font-bold">
                        {formatNumber(entry.pc_ele)}{" "}
                        <span className="text-sm font-normal">
                          {t("dashboard.company.kgCO2")}
                        </span>
                      </span>
                    </div>
                  </div>

                  <div className="bg-cyan-50/50 border-t py-3 px-4">
                    <div className="flex justify-between items-center text-cyan-600 w-full">
                      <div className="flex items-center gap-1.5">
                        <BarChart4 className="h-5 w-5 align-middle" />
                        <span className="align-middle">{t("common.hydricFootprint")}</span>
                      </div>
                      <span className="text-lg font-bold">
                        {formatNumber(entry.ph_ele)}{" "}
                        <span className="text-sm font-normal">
                          m<sup>3</sup>
                        </span>
                      </span>
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>

          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="rounded-lg border border-teal-100 bg-white p-6">
              <div className="flex items-center gap-2 text-teal-700 mb-1">
                <BarChart4 className="h-5 w-5" />
                <span className="text-lg font-medium">{t("common.totalCarbonFootprint")}</span>
              </div>

              <p className="text-xs italic text-teal-600 ml-7 mb-4">{t("footprints.carbonFootprintFormula")}</p>

              <div className="flex justify-end">
                <span className="text-xl font-bold text-teal-700">
                  {formatNumber(calculateTotalElectricityCarbonFootprint(electricityEntries))}{" "}
                  <span>
                    {t("dashboard.company.kgCO2")}
                  </span>
                </span>
              </div>
            </div>

            <div className="rounded-lg border border-cyan-100 bg-white p-6">
              <div className="flex items-center gap-2 text-cyan-600 mb-1">
                <BarChart4 className="h-5 w-5" />
                <span className="text-lg font-medium">{t("common.totalHydricFootprint")}</span>
              </div>

              <p className="text-xs italic text-cyan-600 ml-7 mb-4">{t("footprints.hydricFootprintFormula")}</p>

              <div className="flex justify-end">
                <span className="text-xl font-bold text-cyan-600">
                  {formatNumber(calculateTotalElectricityHydricFootprint(electricityEntries))}{" "}
                  <span>
                    m<sup>3</sup>
                  </span>
                </span>
              </div>
            </div>
          </div>
        </>
      )}

      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <ElectricityModalForm
          onClose={closeModal}
          onSave={handleSave}
          formEntry={formEntry}
          onFormEntryChange={handleFormEntryChange}
          sourceOptions={sourceOptions}
          editing={editingIndex !== null}
        />
      </Dialog>
    </div>
  );
};

export type { ElectricityPayload };
